<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { __ } from '@oat-sa-private/ui-core';
    import { onMount } from 'svelte';
    import { testSessionStatus } from '../../../session/sessionStates.js';
    import { getTestSessionStatusStore } from '../../../testsStateStore.js';
    import { OverlayBox } from '@oat-sa-private/ui-components';
    import DocumentViewer from '@oat-sa-private/ui-components/documentViewer/DocumentViewer.svelte';
    import HtmlViewer from './HtmlViewer.svelte';

    /**
     * This component renders a single item attachment (pdf, image...)
     */

    /**
     * @typedef {Object} Props
     * @property {import('./plugin.js').Attachment} [attachment]
     * @property {Object} assetManager
     * @property {String} serviceCallId
     * @property {Boolean} openInNewTab
     * @property {Boolean} [isAttachmentsPreviewerOpen] - When true, show the attachments card grid; when false, show the selected attachment preview.
     * @property {Function} onBackToAttachments
     * @property {Function} onClose
     * @property {Function} onPageChange
     * @property {Function} onZoomChange
     * @property {Function} onScrollChange
     * @property {Function} onSearchChange
     * @property {Function} onError
     */
    /** @type {Props} */
    let {
        attachment = {},
        assetManager,
        serviceCallId,
        openInNewTab,
        isAttachmentsPreviewerOpen = false,
        onBackToAttachments,
        onClose,
        onPageChange,
        onZoomChange,
        onScrollChange,
        onSearchChange,
        onError
    } = $props();

    // svelte-ignore state_referenced_locally
    const statusStore = getTestSessionStatusStore(serviceCallId);

    let boxElt = $state();

    let attachmentType = $derived(attachment.type?.toLowerCase() || '');
    let startActions = $derived(
        isAttachmentsPreviewerOpen
            ? []
            : [
                  {
                      key: 'arrow-back',
                      icon: 'arrow-left',
                      label: __('Back to attachments'),
                      ariaLabel: __('Back to attachments'),
                      ariaHasPopup: false,
                      ariaExpanded: false,
                      toggled: false
                  }
              ]
    );
    const endActions = [
        {
            key: 'close',
            icon: 'remove',
            label: __('Close attachment'),
            ariaLabel: __('Close attachment')
        }
    ];

    /**
     * Button action handler
     * @param {CustomEvent} e
     */
    function handleOverlayBoxAction(e) {
        const { key } = e.detail;
        if (key === 'arrow-back') {
            e.stopPropagation();
            onBackToAttachments?.();
        } else if (key === 'close') {
            onClose?.();
        }
    }

    onMount(() => {
        boxElt.scrollIntoView?.({
            block: 'nearest',
            inline: 'center',
            behavior: 'smooth'
        });
    });
</script>

<style>
    .attachment-box {
        position: relative;
        --overlay-box-header-height: 5.5rem;

        & :global(.overlay-box-header .heading-container) {
            justify-content: flex-start;
            padding-inline-start: 1rem;
        }
        & :global(.overlay-box-content) {
            padding: 0;
        }
        & .pdf-attachment :global(.document-viewer) {
            --doc-viewer-height: calc(
                var(--testrunner-item-container-height) - var(--testrunner-aside-top-padding) -
                    var(--testrunner-aside-bottom-padding) - var(--overlay-box-header-height) - 1rem
            );
        }
        & .html-attachment :global(.html-viewer) {
            --html-viewer-height: calc(
                var(--testrunner-item-container-height) - var(--testrunner-aside-top-padding) -
                    var(--testrunner-aside-bottom-padding) - var(--overlay-box-header-height) - 1rem
            );
        }
        & .image-attachment {
            max-width: 100%;
        }
    }
</style>

<div class="attachment-box" bind:this={boxElt}>
    {#if $statusStore === testSessionStatus.interacting}
        <OverlayBox
            {startActions}
            {endActions}
            heading={attachment.name}
            headingLevel={3}
            ariaLabel={__('Attachment: %s', attachment.name)}
            role="none"
            inverted={false}
            invertedActions={false}
            on:action={handleOverlayBoxAction}>
            {#key attachment.url}
                {#if attachmentType === 'application/pdf'}
                    <div class="pdf-attachment">
                        <DocumentViewer
                            {...attachment}
                            src={attachment.url}
                            title={attachment.name}
                            options={{ workerSrc: assetManager.resolve('pdf.worker.min.js') }}
                            buttons={{ download: false, openInNewTab, errorOpenInNewTab: true }}
                            on:pagechange={onPageChange}
                            on:zoomchange={onZoomChange}
                            on:scrollchange={onScrollChange}
                            on:searchchange={onSearchChange}
                            on:error={onError} />
                    </div>
                {:else if attachmentType === 'text/html' || attachmentType === 'text/plain'}
                    <div class="html-attachment">
                        <HtmlViewer
                            src={attachment.url}
                            title={attachment.name}
                            buttons={{ errorOpenInNewTab: true }}
                            {onError} />
                    </div>
                {:else if attachmentType.startsWith('image/')}
                    <img class="image-attachment" src={attachment.url} alt={attachment.name} />
                {/if}
            {/key}
        </OverlayBox>
    {/if}
</div>
